import React from 'react';
import { motion } from 'framer-motion';
import { formatDate } from '../utils/dateUtils';

// Calculate days from the milestone date to today
const calculateMilestoneDate = (date, days) => {
  if (!date) return null;
  
  const milestoneDate = new Date(date);
  milestoneDate.setDate(milestoneDate.getDate() + days);
  
  return milestoneDate;
};

const MilestoneCard = ({ days, date, icon, isSpecial, isToday, isPast, isUpcoming, language }) => {
  // Calculate milestone date if date is provided
  const milestoneDate = date ? calculateMilestoneDate(date, days) : null;

  // Card styles based on status
  const getCardStyle = () => {
    if (isToday) {
      return 'border-love-500 bg-love-50 dark:bg-love-900/30 dark:border-love-400 shadow-md';
    } else if (isPast) {
      return 'border-love-300 bg-white dark:bg-gray-750 dark:border-love-800 shadow-sm';
    } else if (isUpcoming) {
      return isSpecial 
        ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 shadow-sm' 
        : 'border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700';
    }
    return 'border-gray-200 dark:border-gray-700';
  };

  return (
    <motion.div 
      className={`rounded-lg border-2 p-4 relative ${getCardStyle()}`}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      {/* Special milestone indicator - Enhanced visibility */}
      {isSpecial && (
        <div className="absolute top-0 left-0 w-10 h-10 flex items-center justify-center">
          <span className="text-2xl text-amber-500 drop-shadow-md">🏆</span>
        </div>
      )}
      
      {/* Status indicator */}
      {isPast && (
        <div className="absolute top-2 right-2 bg-love-500 text-white text-xs px-1.5 py-0.5 rounded-full flex items-center">
          <span className="mr-1">✓</span>
          {language === 'zh' ? '已達成' : 'Achieved'}
        </div>
      )}
      
      {isToday && (
        <div className="absolute top-2 right-2 bg-love-600 text-white text-xs px-1.5 py-0.5 rounded-full flex items-center animate-pulse">
          <span className="mr-1">🎉</span>
          {language === 'zh' ? '今天' : 'Today'}
        </div>
      )}
      
      {isUpcoming && !isToday && (
        <div className="absolute top-2 right-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs px-1.5 py-0.5 rounded-full">
          {language === 'zh' ? '即將到來' : 'Upcoming'}
        </div>
      )}
      
      <div className={`flex items-center space-x-3 ${isSpecial ? 'mt-6' : 'mt-2'}`}>
        <div className={`text-2xl ${isPast ? 'text-love-500' : (isSpecial ? 'text-amber-500' : 'text-gray-400')}`}>
          {icon}
        </div>
        
        <div>
          <h4 className={`font-medium ${isPast ? 'text-gray-800 dark:text-gray-200' : (isSpecial ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-gray-400')}`}>
            {language === 'zh' ? `${days} 天紀念日` : `${days} Days Milestone`}
            {isSpecial && <span className="ml-2 text-amber-500">★</span>}
          </h4>
          
          <p className={`text-sm ${isPast ? 'text-gray-600 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}`}>
            {milestoneDate ? formatDate(milestoneDate, language) : ''}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default MilestoneCard;